import { FieldLabel } from "@puckeditor/core";
import type { CSSProperties, ReactNode } from "react";

import {
  generateFlexCss,
  type FlexConfig,
} from "@/components/pages/dss/flex-controls";
import {
  generateGridCss,
  type GridConfig,
} from "@/components/pages/dss/grid-controls";

type DisplayMode = "block" | "flex" | "grid";

interface PuckCustomFieldProps<TField> {
  field: TField;
  onChange: (value: unknown, uiState?: Record<string, unknown>) => void;
  value: unknown;
  readOnly?: boolean;
}

interface FlexGridFieldConfig {
  label: string;
}

interface GapValue {
  value: number;
  unit: "px" | "rem" | "%";
}

export interface FlexGridFieldValue {
  display: DisplayMode;
  flex: FlexConfig;
  grid: GridConfig;
}

const DEFAULT_GAP: GapValue = { value: 0, unit: "px" };

export const DEFAULT_FLEX_GRID_VALUE: FlexGridFieldValue = {
  display: "block",
  flex: {
    direction: "row",
    wrap: "nowrap",
    justifyContent: "flex-start",
    alignItems: "stretch",
    alignContent: "stretch",
    rowGap: DEFAULT_GAP,
    columnGap: DEFAULT_GAP,
  },
  grid: {
    columns: 2,
    customColumns: "",
    rowMode: "auto",
    rowCount: 1,
    customRows: "",
    rowGap: DEFAULT_GAP,
    columnGap: DEFAULT_GAP,
    templateAreas: "",
  },
};

const GRID_PRESETS = [
  { id: "equal-2", label: "Equal", columns: 2, template: "1fr 1fr" },
  { id: "two-one", label: "2:1", columns: 2, template: "2fr 1fr" },
  { id: "one-two", label: "1:2", columns: 2, template: "1fr 2fr" },
  { id: "triple", label: "1:1:1", columns: 3, template: "1fr 1fr 1fr" },
  { id: "sidebar", label: "Sidebar", columns: 2, template: "280px 1fr" },
] as const;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function readGapValue(value: unknown): GapValue {
  if (!isRecord(value)) {
    return DEFAULT_GAP;
  }

  const rawValue = Number(value.value);
  const unit = value.unit === "rem" || value.unit === "%" ? value.unit : "px";
  return {
    value: Number.isFinite(rawValue) ? Math.max(0, rawValue) : 0,
    unit,
  };
}

function readFlexConfig(value: unknown): FlexConfig {
  if (!isRecord(value)) {
    return DEFAULT_FLEX_GRID_VALUE.flex;
  }

  return {
    direction:
      value.direction === "row-reverse" ||
      value.direction === "column" ||
      value.direction === "column-reverse"
        ? value.direction
        : "row",
    wrap:
      value.wrap === "wrap" || value.wrap === "wrap-reverse"
        ? value.wrap
        : "nowrap",
    justifyContent:
      value.justifyContent === "center" ||
      value.justifyContent === "flex-end" ||
      value.justifyContent === "space-between" ||
      value.justifyContent === "space-around" ||
      value.justifyContent === "space-evenly"
        ? value.justifyContent
        : "flex-start",
    alignItems:
      value.alignItems === "flex-start" ||
      value.alignItems === "center" ||
      value.alignItems === "flex-end" ||
      value.alignItems === "baseline"
        ? value.alignItems
        : "stretch",
    alignContent:
      value.alignContent === "flex-start" ||
      value.alignContent === "center" ||
      value.alignContent === "flex-end" ||
      value.alignContent === "space-between" ||
      value.alignContent === "space-around"
        ? value.alignContent
        : "stretch",
    rowGap: readGapValue(value.rowGap),
    columnGap: readGapValue(value.columnGap),
  };
}

function readGridConfig(value: unknown): GridConfig {
  if (!isRecord(value)) {
    return DEFAULT_FLEX_GRID_VALUE.grid;
  }

  const rawColumns = Number(value.columns);
  const rawRowCount = Number(value.rowCount);
  return {
    columns: Number.isFinite(rawColumns) ? Math.min(Math.max(Math.round(rawColumns), 1), 12) : 2,
    customColumns: typeof value.customColumns === "string" ? value.customColumns : "",
    rowMode:
      value.rowMode === "explicit" || value.rowMode === "custom"
        ? value.rowMode
        : "auto",
    rowCount: Number.isFinite(rawRowCount) ? Math.min(Math.max(Math.round(rawRowCount), 1), 24) : 1,
    customRows: typeof value.customRows === "string" ? value.customRows : "",
    rowGap: readGapValue(value.rowGap),
    columnGap: readGapValue(value.columnGap),
    templateAreas: typeof value.templateAreas === "string" ? value.templateAreas : "",
  };
}

export function normalizeFlexGridFieldValue(value: unknown): FlexGridFieldValue {
  if (!isRecord(value)) {
    return DEFAULT_FLEX_GRID_VALUE;
  }

  return {
    display: value.display === "flex" || value.display === "grid" ? value.display : "block",
    flex: readFlexConfig(value.flex),
    grid: readGridConfig(value.grid),
  };
}

function toCssLength(value: GapValue): string | undefined {
  if (value.value <= 0) {
    return undefined;
  }

  return `${value.value}${value.unit}`;
}

export function buildFlexGridStyles(value: unknown): CSSProperties {
  const config = normalizeFlexGridFieldValue(value);

  if (config.display === "flex") {
    return {
      display: "flex",
      flexDirection: config.flex.direction,
      flexWrap: config.flex.wrap,
      justifyContent: config.flex.justifyContent,
      alignItems: config.flex.alignItems,
      alignContent: config.flex.wrap === "nowrap" ? undefined : config.flex.alignContent,
      rowGap: toCssLength(config.flex.rowGap),
      columnGap: toCssLength(config.flex.columnGap),
    };
  }

  if (config.display === "grid") {
    return {
      display: "grid",
      gridTemplateColumns: config.grid.customColumns.trim() || `repeat(${config.grid.columns}, minmax(0, 1fr))`,
      gridTemplateRows:
        config.grid.rowMode === "explicit"
          ? `repeat(${config.grid.rowCount}, minmax(0, auto))`
          : config.grid.rowMode === "custom" && config.grid.customRows.trim()
            ? config.grid.customRows.trim()
            : undefined,
      gridTemplateAreas: config.grid.templateAreas.trim() || undefined,
      rowGap: toCssLength(config.grid.rowGap),
      columnGap: toCssLength(config.grid.columnGap),
    };
  }

  return { display: "block" };
}

export function createFlexGridField(config: FlexGridFieldConfig) {
  return {
    type: "custom" as const,
    ...config,
    render: FlexGridFieldEditor,
  };
}

function LayoutButton({
  active,
  disabled,
  label,
  onClick,
  children,
}: {
  active: boolean;
  disabled?: boolean;
  label: string;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      aria-pressed={active}
      disabled={disabled}
      onClick={onClick}
      className={[
        "flex h-9 w-11 items-center justify-center rounded-md border transition-colors disabled:cursor-not-allowed disabled:opacity-50",
        active
          ? "border-[var(--color-primary)] bg-[var(--color-primary)]/10 text-[var(--color-primary)]"
          : "border-[var(--color-border)] text-[var(--color-muted-foreground)] hover:border-[var(--color-primary)]/50",
      ].join(" ")}
    >
      {children}
    </button>
  );
}

function AxisGlyph({
  orientation,
  reverse = false,
  distribution = "start",
}: {
  orientation: "row" | "column";
  reverse?: boolean;
  distribution?: "start" | "center" | "end" | "between" | "around" | "evenly";
}) {
  const positions =
    distribution === "center"
      ? ["30%", "50%", "70%"]
      : distribution === "end"
        ? ["45%", "65%", "85%"]
        : distribution === "between"
          ? ["15%", "50%", "85%"]
          : distribution === "around"
            ? ["22%", "50%", "78%"]
            : distribution === "evenly"
              ? ["18%", "50%", "82%"]
              : ["15%", "35%", "55%"];

  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.7">
      <rect x="4" y="4" width="16" height="16" rx="2" />
      {positions.map((position, index) => (
        orientation === "row" ? (
          <rect
            key={position}
            x={position}
            y={reverse ? 14 - index : 8 + index * 2}
            width="2"
            height="6"
            fill="currentColor"
            stroke="none"
          />
        ) : (
          <rect
            key={position}
            x={reverse ? 14 - index * 2 : 8 + index * 2}
            y={position}
            width="6"
            height="2"
            fill="currentColor"
            stroke="none"
          />
        )
      ))}
    </svg>
  );
}

function AlignGlyph({ mode }: { mode: "start" | "center" | "end" | "stretch" | "baseline" }) {
  const xPositions =
    mode === "center"
      ? [9, 9, 9]
      : mode === "end"
        ? [12, 12, 12]
        : mode === "stretch"
          ? [7, 7, 7]
          : mode === "baseline"
            ? [7, 9, 12]
            : [6, 6, 6];
  const widths =
    mode === "stretch"
      ? [10, 10, 10]
      : mode === "baseline"
        ? [6, 4, 3]
        : [6, 6, 6];

  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.7">
      <rect x="4" y="4" width="16" height="16" rx="2" />
      {mode === "baseline" && <line x1="6" y1="17" x2="18" y2="17" />}
      {[0, 1, 2].map((index) => (
        <rect
          key={index}
          x={xPositions[index]}
          y={6 + index * 4}
          width={widths[index]}
          height="2"
          fill="currentColor"
          stroke="none"
        />
      ))}
    </svg>
  );
}

function GridPresetGlyph({ columns }: { columns: number }) {
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.7">
      <rect x="4" y="4" width="16" height="16" rx="2" />
      {Array.from({ length: columns - 1 }, (_, index) => (
        <line
          key={index}
          x1={4 + ((index + 1) * 16) / columns}
          y1="4"
          x2={4 + ((index + 1) * 16) / columns}
          y2="20"
        />
      ))}
      <line x1="4" y1="12" x2="20" y2="12" />
    </svg>
  );
}

function GapInput({
  label,
  value,
  onChange,
  readOnly,
}: {
  label: string;
  value: number;
  onChange: (next: number) => void;
  readOnly?: boolean;
}) {
  return (
    <label className="flex flex-col gap-1 text-[10px] font-medium text-[var(--color-muted-foreground)]">
      {label}
      <input
        type="number"
        min={0}
        step={8}
        value={value}
        disabled={readOnly}
        onChange={(event) => onChange(Math.max(0, Number(event.target.value) || 0))}
        className="h-8 rounded-md border border-[var(--color-border)] bg-[var(--color-background)] px-2 text-[11px] text-[var(--color-foreground)]"
      />
    </label>
  );
}

function FlexGridFieldEditor({
  field,
  onChange,
  readOnly,
  value,
}: PuckCustomFieldProps<FlexGridFieldConfig>) {
  const current = normalizeFlexGridFieldValue(value);

  const update = (next: Partial<FlexGridFieldValue>) => {
    onChange({
      ...current,
      ...next,
    });
  };

  const updateFlex = (next: Partial<FlexConfig>) => {
    onChange({
      ...current,
      flex: {
        ...current.flex,
        ...next,
      },
    });
  };

  const updateGrid = (next: Partial<GridConfig>) => {
    onChange({
      ...current,
      grid: {
        ...current.grid,
        ...next,
      },
    });
  };

  const cssPreview =
    current.display === "flex"
      ? generateFlexCss(current.flex)
      : current.display === "grid"
        ? generateGridCss(current.grid)
        : "display: block;";

  return (
    <FieldLabel label={field.label}>
      <div className="space-y-3 rounded-lg border border-border bg-background/60 p-3">
        <div className="space-y-1">
          <div className="text-[11px] font-semibold text-foreground">Layout mode</div>
          <div className="flex gap-1.5">
            <LayoutButton
              active={current.display === "block"}
              disabled={readOnly}
              label="Block layout"
              onClick={() => update({ display: "block" })}
            >
              <AxisGlyph orientation="column" />
            </LayoutButton>
            <LayoutButton
              active={current.display === "flex"}
              disabled={readOnly}
              label="Flex layout"
              onClick={() => update({ display: "flex" })}
            >
              <AxisGlyph orientation="row" distribution="between" />
            </LayoutButton>
            <LayoutButton
              active={current.display === "grid"}
              disabled={readOnly}
              label="Grid layout"
              onClick={() => update({ display: "grid" })}
            >
              <GridPresetGlyph columns={2} />
            </LayoutButton>
          </div>
        </div>

        {current.display === "flex" && (
          <div className="space-y-3 rounded-md border border-[var(--color-border)] bg-[var(--color-muted)]/20 p-3">
            <div className="text-[11px] font-semibold text-foreground">Flex controls</div>

            <div className="space-y-1">
              <div className="text-[10px] font-medium text-[var(--color-muted-foreground)]">Direction</div>
              <div className="flex flex-wrap gap-1">
                {[
                  { value: "row", label: "Row", icon: <AxisGlyph orientation="row" /> },
                  { value: "column", label: "Column", icon: <AxisGlyph orientation="column" /> },
                  { value: "row-reverse", label: "Row reverse", icon: <AxisGlyph orientation="row" reverse /> },
                  { value: "column-reverse", label: "Column reverse", icon: <AxisGlyph orientation="column" reverse /> },
                ].map((option) => (
                  <LayoutButton
                    key={option.value}
                    active={current.flex.direction === option.value}
                    disabled={readOnly}
                    label={option.label}
                    onClick={() => updateFlex({ direction: option.value as FlexConfig["direction"] })}
                  >
                    {option.icon}
                  </LayoutButton>
                ))}
              </div>
            </div>

            <div className="space-y-1">
              <div className="text-[10px] font-medium text-[var(--color-muted-foreground)]">Wrap</div>
              <div className="flex gap-1">
                {[
                  { value: "nowrap", label: "No wrap" },
                  { value: "wrap", label: "Wrap" },
                ].map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    disabled={readOnly}
                    aria-pressed={current.flex.wrap === option.value}
                    onClick={() => updateFlex({ wrap: option.value as FlexConfig["wrap"] })}
                    className={[
                      "rounded-md border px-2 py-1 text-[10px] font-medium transition-colors",
                      current.flex.wrap === option.value
                        ? "border-[var(--color-primary)] bg-[var(--color-primary)]/10 text-[var(--color-primary)]"
                        : "border-[var(--color-border)] text-[var(--color-muted-foreground)] hover:border-[var(--color-primary)]/50",
                    ].join(" ")}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-1">
              <div className="text-[10px] font-medium text-[var(--color-muted-foreground)]">Justify</div>
              <div className="flex flex-wrap gap-1">
                {[
                  { value: "flex-start", label: "Start", distribution: "start" },
                  { value: "center", label: "Center", distribution: "center" },
                  { value: "flex-end", label: "End", distribution: "end" },
                  { value: "space-between", label: "Between", distribution: "between" },
                  { value: "space-around", label: "Around", distribution: "around" },
                  { value: "space-evenly", label: "Evenly", distribution: "evenly" },
                ].map((option) => (
                  <LayoutButton
                    key={option.value}
                    active={current.flex.justifyContent === option.value}
                    disabled={readOnly}
                    label={option.label}
                    onClick={() => updateFlex({ justifyContent: option.value as FlexConfig["justifyContent"] })}
                  >
                    <AxisGlyph orientation="row" distribution={option.distribution as never} />
                  </LayoutButton>
                ))}
              </div>
            </div>

            <div className="space-y-1">
              <div className="text-[10px] font-medium text-[var(--color-muted-foreground)]">Align</div>
              <div className="flex flex-wrap gap-1">
                {[
                  { value: "flex-start", label: "Start", glyph: <AlignGlyph mode="start" /> },
                  { value: "center", label: "Center", glyph: <AlignGlyph mode="center" /> },
                  { value: "flex-end", label: "End", glyph: <AlignGlyph mode="end" /> },
                  { value: "stretch", label: "Stretch", glyph: <AlignGlyph mode="stretch" /> },
                  { value: "baseline", label: "Baseline", glyph: <AlignGlyph mode="baseline" /> },
                ].map((option) => (
                  <LayoutButton
                    key={option.value}
                    active={current.flex.alignItems === option.value}
                    disabled={readOnly}
                    label={option.label}
                    onClick={() => updateFlex({ alignItems: option.value as FlexConfig["alignItems"] })}
                  >
                    {option.glyph}
                  </LayoutButton>
                ))}
              </div>
            </div>

            <GapInput
              label="Gap (px)"
              value={current.flex.columnGap.value}
              readOnly={readOnly}
              onChange={(next) =>
                updateFlex({
                  rowGap: { value: next, unit: "px" },
                  columnGap: { value: next, unit: "px" },
                })}
            />
          </div>
        )}

        {current.display === "grid" && (
          <div className="space-y-3 rounded-md border border-[var(--color-border)] bg-[var(--color-muted)]/20 p-3">
            <div className="text-[11px] font-semibold text-foreground">Grid controls</div>

            <div className="space-y-1">
              <div className="text-[10px] font-medium text-[var(--color-muted-foreground)]">Columns</div>
              <div className="flex flex-wrap gap-1">
                {Array.from({ length: 6 }, (_, index) => {
                  const columnCount = index + 1;
                  return (
                    <LayoutButton
                      key={columnCount}
                      active={current.grid.columns === columnCount && current.grid.customColumns.trim().length === 0}
                      disabled={readOnly}
                      label={`${columnCount} columns`}
                      onClick={() => updateGrid({ columns: columnCount, customColumns: "" })}
                    >
                      <GridPresetGlyph columns={columnCount} />
                    </LayoutButton>
                  );
                })}
              </div>
            </div>

            <div className="space-y-1">
              <div className="text-[10px] font-medium text-[var(--color-muted-foreground)]">Rows</div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  disabled={readOnly}
                  aria-pressed={current.grid.rowMode === "auto"}
                  onClick={() => updateGrid({ rowMode: "auto" })}
                  className={[
                    "rounded-md border px-2 py-1 text-[10px] font-medium transition-colors",
                    current.grid.rowMode === "auto"
                      ? "border-[var(--color-primary)] bg-[var(--color-primary)]/10 text-[var(--color-primary)]"
                      : "border-[var(--color-border)] text-[var(--color-muted-foreground)] hover:border-[var(--color-primary)]/50",
                  ].join(" ")}
                >
                  Auto
                </button>
                <button
                  type="button"
                  disabled={readOnly}
                  aria-pressed={current.grid.rowMode === "explicit"}
                  onClick={() => updateGrid({ rowMode: "explicit" })}
                  className={[
                    "rounded-md border px-2 py-1 text-[10px] font-medium transition-colors",
                    current.grid.rowMode === "explicit"
                      ? "border-[var(--color-primary)] bg-[var(--color-primary)]/10 text-[var(--color-primary)]"
                      : "border-[var(--color-border)] text-[var(--color-muted-foreground)] hover:border-[var(--color-primary)]/50",
                  ].join(" ")}
                >
                  Count
                </button>
                {current.grid.rowMode === "explicit" && (
                  <input
                    type="number"
                    min={1}
                    max={6}
                    value={current.grid.rowCount}
                    disabled={readOnly}
                    onChange={(event) =>
                      updateGrid({
                        rowCount: Math.min(Math.max(Number(event.target.value) || 1, 1), 6),
                      })}
                    className="h-8 w-16 rounded-md border border-[var(--color-border)] bg-[var(--color-background)] px-2 text-[11px] text-[var(--color-foreground)]"
                  />
                )}
              </div>
            </div>

            <GapInput
              label="Gap (px)"
              value={current.grid.columnGap.value}
              readOnly={readOnly}
              onChange={(next) =>
                updateGrid({
                  rowGap: { value: next, unit: "px" },
                  columnGap: { value: next, unit: "px" },
                })}
            />

            <div className="space-y-1">
              <div className="text-[10px] font-medium text-[var(--color-muted-foreground)]">Template presets</div>
              <div className="flex flex-wrap gap-1">
                {GRID_PRESETS.map((preset) => (
                  <button
                    key={preset.id}
                    type="button"
                    disabled={readOnly}
                    onClick={() =>
                      updateGrid({
                        columns: preset.columns,
                        customColumns: preset.template,
                      })}
                    className={[
                      "rounded-md border px-2 py-1 text-[10px] font-medium transition-colors",
                      current.grid.customColumns.trim() === preset.template
                        ? "border-[var(--color-primary)] bg-[var(--color-primary)]/10 text-[var(--color-primary)]"
                        : "border-[var(--color-border)] text-[var(--color-muted-foreground)] hover:border-[var(--color-primary)]/50",
                    ].join(" ")}
                  >
                    {preset.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        <div className="rounded-md bg-[var(--color-muted)] px-3 py-2">
          <pre className="overflow-x-auto whitespace-pre-wrap text-[10px] text-[var(--color-muted-foreground)]">
            {cssPreview}
          </pre>
        </div>
      </div>
    </FieldLabel>
  );
}
